import { Show, createMemo, createSignal, onCleanup } from 'solid-js';
import qrcode from 'qrcode-generator';
import { Device } from '../api';

const PLAYER = (import.meta.env.VITE_PLAYER_BASE as string | undefined) ?? 'https://scai.fm';

/** The QR code as an SVG path: drawn here, so the pairing code never leaves the browser. */
function Qr(props: { text: string }) {
  const qr = createMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(props.text);
    q.make();
    const n = q.getModuleCount();
    let d = '';
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (q.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
    return { n, d };
  });
  return (
    <svg viewBox={`-4 -4 ${qr().n + 8} ${qr().n + 8}`} class="h-44 w-44 rounded-lg bg-white" role="img" aria-label="QR code that opens the pairing page with this code">
      <path d={qr().d} fill="#000" shape-rendering="crispEdges" />
    </svg>
  );
}

/** Shown right after "Pair device" (or "Show code"): code, QR, countdown, and the moment the device pairs. */
export default function PairingPanel(props: { device: Device; zone: string; onRenew: () => void; onClose: () => void; busy: boolean }) {
  const [now, setNow] = createSignal(Date.now());
  const t = setInterval(() => setNow(Date.now()), 1000);
  onCleanup(() => clearInterval(t));
  const left = () => (props.device.pairing_expires_at ? new Date(props.device.pairing_expires_at + 'Z').getTime() - now() : 0);
  const clock = () => { const s = Math.max(0, Math.ceil(left() / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
  const url = () => `${PLAYER}/pair?code=${props.device.pairing_code}`;

  return (
    <div class="card-z mb-4 flex flex-col gap-4 border-2 border-amber sm:flex-row sm:items-center" role="region" aria-label={`Pairing ${props.device.name}`} aria-live="polite">
      <Show when={!props.device.paired} fallback={
        <div class="flex flex-1 items-center gap-3">
          <span class="text-3xl text-success" aria-hidden="true">✓</span>
          <div><div class="font-semibold">{props.device.name} is paired</div>
            <div class="text-sm text-ink-3">It plays {props.zone} and shows up in the list below.</div></div>
        </div>}>
        <Show when={props.device.pairing_code && left() > 0} fallback={
          <div class="flex-1">
            <div class="font-semibold">The code for {props.device.name} has expired</div>
            <div class="text-sm text-ink-3">Codes are valid for 10 minutes. Get a new one and pair again.</div>
            <button class="btn btn-sm btn-secondary mt-3" disabled={props.busy} onClick={props.onRenew}>New code</button>
          </div>}>
          <Qr text={url()} />
          <div class="flex-1">
            <div class="text-sm text-ink-3">Pairing code for <b class="text-ink">{props.device.name}</b> · {props.zone}</div>
            <code class="mono my-2 block text-4xl font-bold tracking-[0.3em]">{props.device.pairing_code}</code>
            <ol class="list-decimal space-y-1 pl-5 text-sm">
              <li>Scan the QR code with the tablet's camera, <b>or</b></li>
              <li>open <span class="mono">scai.fm/pair</span> in its browser and type the code.</li>
            </ol>
            <div class="mt-2 text-sm text-ink-3"><span class="loading loading-dots loading-xs align-middle" aria-hidden="true" /> Waiting for the device · valid for {clock()}</div>
          </div>
        </Show>
      </Show>
      <button class="btn btn-ghost btn-sm self-start" onClick={props.onClose}>{props.device.paired ? 'Done' : 'Close'}</button>
    </div>
  );
}
